import { FC, useState } from 'react';
import { get } from 'lodash';
import { Modal } from 'components/Base/Modal';
import ConfirmModalBody from 'components/Shared/ConfirmModalBody';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import IconButton from 'components/Library/Buttons/IconButton';
import { useDeleteCustomWorkshopTaskMutation } from 'redux/apiSlice/workshopTasks.slice';
import { DeleteTrashOutline } from 'assets/icon/icon';
import { TDeleteCustomTaskProps } from './DeleteCustomTask.types';

// Row action in the Tasks table Actions column (GROW-4338), rendered only for
// custom tasks next to Edit. Seeded tasks can't be deleted, and the BE rejects
// the call for them too.
const DeleteCustomTask: FC<TDeleteCustomTaskProps> = ({
  workshopId,
  taskId,
  taskName,
  onDeleted,
}) => {
  const { showToastNotification } = useShowToastNotification();
  const [open, setOpen] = useState(false);
  const [deleteTask, { isLoading }] = useDeleteCustomWorkshopTaskMutation();

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    try {
      await deleteTask({ workshopId, taskId }).unwrap();
      onDeleted?.(taskId);
      showToastNotification({
        message: 'Task has been successfully deleted',
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (error) {
      // Keep the modal open on API errors so the user can retry or cancel.
      showToastNotification({
        message:
          get(error, 'data.message') ||
          'Could not delete the task. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <>
      <IconButton
        icon={DeleteTrashOutline}
        size={16}
        defaultFill="#656565"
        hoverFill="#333333"
        onClick={() => setOpen(true)}
        ariaLabel="Delete task"
      />
      {open && (
        <Modal
          title="Delete Task"
          open={open}
          onCancel={handleClose}
          onOk={handleSubmit}
          okText="Delete"
          cancelText="Cancel"
          confirmLoading={isLoading}
        >
          <ConfirmModalBody
            description={`Are you sure you want to delete "${taskName}"? This cannot be undone.`}
          />
        </Modal>
      )}
    </>
  );
};

export default DeleteCustomTask;
